import axios from 'axios';

class InterceptorHandler {
  constructor(interceptor, handler) {
    this.interceptor = interceptor;
    this.handler = handler;
  }
  handle(request) {
    return this.interceptor.intercept(request, this.handler);
  }
}

var Code = /* @__PURE__ */ ((Code2) => {
  Code2["OK"] = "OK";
  Code2["CANCELLED"] = "CANCELLED";
  Code2["UNKNOWN"] = "UNKNOWN";
  Code2["INVALID_ARGUMENT"] = "INVALID_ARGUMENT";
  Code2["DEADLINE_EXCEEDED"] = "DEADLINE_EXCEEDED";
  Code2["NOT_FOUND"] = "NOT_FOUND";
  Code2["ALREADY_EXISTS"] = "ALREADY_EXISTS";
  Code2["PERMISSION_DENIED"] = "PERMISSION_DENIED";
  Code2["UNAUTHENTICATED"] = "UNAUTHENTICATED";
  Code2["RESOURCE_EXHAUSTED"] = "RESOURCE_EXHAUSTED";
  Code2["FAILED_PRECONDITION"] = "FAILED_PRECONDITION";
  Code2["ABORTED"] = "ABORTED";
  Code2["OUT_OF_RANGE"] = "OUT_OF_RANGE";
  Code2["UNIMPLEMENTED"] = "UNIMPLEMENTED";
  Code2["INTERNAL"] = "INTERNAL";
  Code2["UNAVAILABLE"] = "UNAVAILABLE";
  Code2["DATA_LOSS"] = "DATA_LOSS";
  return Code2;
})(Code || {});

var AxiosErrorCode = /* @__PURE__ */ ((AxiosErrorCode2) => {
  AxiosErrorCode2["ECONNABORTED"] = "ECONNABORTED";
  return AxiosErrorCode2;
})(AxiosErrorCode || {});

class APIError {
  constructor(error) {
    this.name = "APIError";
    this.message = "APIError";
    this.code = Code.UNKNOWN;
    this.code = codeFrom(error);
    this.message = messageFrom(error);
    const response = error.response;
    if (response && response.status) {
      this.status = response.status;
    }
    if (response && response.data) {
      this.details = response.data.details;
      this.requestId = response.data.requestId;
    }
  }
  get asObject() {
    return {
      name: "APIError",
      message: this.message,
      code: this.code,
      status: this.status,
      details: this.details,
      requestId: this.requestId
    };
  }
}
function messageFrom(error) {
  if (error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }
  return error.message;
}
function codeFrom(error) {
  if (error.response && error.response.data && error.response.data.code !== void 0) {
    return codeFromGRPCCode(error.response.data.code);
  }
  if (error.response && error.response.status) {
    return codeFromHTTPStatus(error.response.status);
  }
  if (error.code) {
    return codeFromAxiosErrorCode(error.code);
  }
  return Code.UNKNOWN;
}
function codeFromHTTPStatus(status) {
  switch (status) {
    case 200:
      return Code.OK;
    case 499:
    case 408:
      return Code.CANCELLED;
    case 400:
      return Code.INVALID_ARGUMENT;
    case 504:
      return Code.DEADLINE_EXCEEDED;
    case 404:
      return Code.NOT_FOUND;
    case 409:
      return Code.ALREADY_EXISTS;
    case 403:
      return Code.PERMISSION_DENIED;
    case 429:
      return Code.RESOURCE_EXHAUSTED;
    case 412:
      return Code.FAILED_PRECONDITION;
    case 501:
      return Code.UNIMPLEMENTED;
    case 500:
      return Code.INTERNAL;
    case 503:
      return Code.UNAVAILABLE;
    case 401:
      return Code.UNAUTHENTICATED;
    default:
      return Code.UNKNOWN;
  }
}
function codeFromGRPCCode(code) {
  switch (code) {
    case 0:
      return Code.OK;
    case 1:
      return Code.CANCELLED;
    case 2:
      return Code.UNKNOWN;
    case 3:
      return Code.INVALID_ARGUMENT;
    case 4:
      return Code.DEADLINE_EXCEEDED;
    case 5:
      return Code.NOT_FOUND;
    case 6:
      return Code.ALREADY_EXISTS;
    case 7:
      return Code.PERMISSION_DENIED;
    case 8:
      return Code.RESOURCE_EXHAUSTED;
    case 9:
      return Code.FAILED_PRECONDITION;
    case 10:
      return Code.ABORTED;
    case 11:
      return Code.OUT_OF_RANGE;
    case 12:
      return Code.UNIMPLEMENTED;
    case 13:
      return Code.INTERNAL;
    case 14:
      return Code.UNAVAILABLE;
    case 15:
      return Code.DATA_LOSS;
    case 16:
      return Code.UNAUTHENTICATED;
    default:
      return Code.UNKNOWN;
  }
}
function codeFromAxiosErrorCode(code) {
  switch (code) {
    case AxiosErrorCode.ECONNABORTED:
      return Code.CANCELLED;
    default:
      return Code.UNKNOWN;
  }
}

function isPreferQueryParameter(method) {
  switch (method) {
    case "get":
    case "GET":
    case "head":
    case "HEAD":
    case "delete":
    case "DELETE":
      return true;
    default:
      return false;
  }
}

class AxiosBackend {
  constructor(options) {
    this.client = axios.create();
    this.baseURL = options.baseURL;
    this.timeoutDuration = (options.timeoutDuration || 15) * 1e3;
  }
  handle(request) {
    return new Promise((resolve, reject) => {
      this.client.request(this.makeRequest(request)).then((res) => resolve(this.makeResponse(request, res))).catch((error) => reject(this.makeError(error)));
    });
  }
  makeRequest(request) {
    const isQueryParameter = isPreferQueryParameter(request.method);
    return {
      url: request.path,
      proxy: false,
      method: request.method,
      params: isQueryParameter && request.parameter,
      data: !isQueryParameter && request.parameter,
      withCredentials: request.withCredentials === true,
      timeout: request.timeoutDuration || this.timeoutDuration,
      baseURL: request.baseURL || this.baseURL,
      headers: request.headerFields
    };
  }
  makeResponse(request, response) {
    return request.parse ? request.parse(response) : response.data;
  }
  makeError(error) {
    return new APIError(error);
  }
}

class APIClient {
  constructor(backend) {
    this.backend = backend;
    this.interceptors = [];
    this.backend = backend;
  }
  static initWithOptions(options) {
    const backend = new AxiosBackend(options);
    return new this(backend);
  }
  static initWithBackend(backend) {
    return new this(backend);
  }
  request(request) {
    const handler = this.interceptors.reduceRight((handler2, interceptor) => {
      return new InterceptorHandler(interceptor, handler2);
    }, this.backend);
    return handler.handle(request);
  }
  use(interceptor) {
    this.interceptors.push(interceptor);
  }
}

var APIBackend;
((APIBackend2) => {
  APIBackend2.DefaultOptions = {
    baseURL: "/",
    timeoutDuration: 10
  };
})(APIBackend || (APIBackend = {}));

var UnauthenticatedInfo;
((UnauthenticatedInfo2) => {
  ((UnauthenticatedType2) => {
    UnauthenticatedType2["NONE"] = "NONE";
    UnauthenticatedType2["INVALID_TOKEN"] = "INVALID_TOKEN";
    UnauthenticatedType2["EXPIRED_TOKEN"] = "EXPIRED_TOKEN";
    UnauthenticatedType2["INVALID_IP"] = "INVALID_IP";
    UnauthenticatedType2["PASSCODE_NOT_VERIFIED"] = "PASSCODE_NOT_VERIFIED";
    UnauthenticatedType2["SMS_VERIFICATION_REQUIRED"] = "SMS_VERIFICATION_REQUIRED";
    UnauthenticatedType2["FIDO_UV_VERIFICATION_REQUIRED"] = "FIDO_UV_VERIFICATION_REQUIRED";
    UnauthenticatedType2["INVALID_DPOP_PROOF_JWT"] = "INVALID_DPOP_PROOF_JWT";
    UnauthenticatedType2["INVALID_AUDIENCE"] = "INVALID_AUDIENCE";
    UnauthenticatedType2["INSUFFICIENT_SCOPE"] = "INSUFFICIENT_SCOPE";
    UnauthenticatedType2["INSUFFICIENT_ADDITIONAL_AUTHENTICATION"] = "INSUFFICIENT_ADDITIONAL_AUTHENTICATION";
  })(UnauthenticatedInfo2.UnauthenticatedType || (UnauthenticatedInfo2.UnauthenticatedType = {}));
})(UnauthenticatedInfo || (UnauthenticatedInfo = {}));

export { APIBackend, APIClient, APIError, AxiosBackend, AxiosErrorCode, Code, InterceptorHandler, UnauthenticatedInfo, isPreferQueryParameter };
